import React, { useState, useCallback, useEffect } from 'react';
import { Input } from './Input';
import { usersAPI } from '../../services/api';
import { FormLabel } from './Dialog';
import { Locate, Error as ErrorIcon } from '../Icons/CustomIcons';

/**
 * AddressInput
 *
 * Props:
 *   street, plz, city         — aktuelle Feldwerte
 *   onChange({ street, plz, city })  — wird bei jeder Änderung aufgerufen
 *   onValidationChange(status, coords?)
 *     status: 'idle' | 'checking' | 'valid' | 'invalid'
 *     coords: { lat, lng } — nur bei status === 'valid'
 *   validateRef  — Ref, in den eine async validate()-Funktion eingehängt wird;
 *                  gibt null (ok) oder einen Fehlerstring zurück
 *   required     — macht Strasse required
 *   onInvalid, onInput  — HTML-Formvalidierungs-Handler
 */
const AddressInput = ({
  street = '',
  plz = '',
  city = '',
  onChange,
  onValidationChange,
  validateRef,
  required = false,
  onInvalid,
  onInput,
  className = '',
  geolocation = false,
}) => {
  const [streetTouched, setStreetTouched] = useState(false);
  const [cityTouched, setCityTouched] = useState(false);
  const [validationStatus, setValidationStatus] = useState('idle');
  const [geoLoading, setGeoLoading] = useState(false);

  const handleGeolocate = () => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { 'Accept-Language': 'de' } }
          );
          const data = await res.json();
          const a = data.address || {};
          onChange?.({
            street: `${a.road || ''}${a.house_number ? ' ' + a.house_number : ''}`.trim(),
            plz: a.postcode || '',
            city: a.city || a.town || a.village || '',
          });
        } catch {
          // ignore
        }
        setGeoLoading(false);
      },
      () => setGeoLoading(false)
    );
  };

  const streetEmpty = !street.trim();
  const cityEmpty = !city.trim();

  const notifyStatus = useCallback((status, coords) => {
    setValidationStatus(status);
    onValidationChange?.(status, coords);
  }, [onValidationChange]);

  // Wird von außen aufgerufen (z.B. beim Klick auf "Datum & Uhrzeit").
  // Gibt null zurück wenn gültig, sonst einen Fehlerstring.
  const validate = useCallback(async () => {
    if (!street.trim()) return 'Bitte geben Sie eine Strasse ein.';
    const plzCity = [plz.trim(), city.trim()].filter(Boolean).join(' ');
    const combined = [street.trim(), plzCity].filter(Boolean).join(', ');
    notifyStatus('checking');
    try {
      const response = await usersAPI.geocode(combined);
      const { valid, lat, lng } = response.data;
      if (valid) {
        notifyStatus('valid', { lat, lng });
        return null;
      }
      notifyStatus('invalid');
      return 'Ungültige Adresse. Bitte Strasse, PLZ und Ort prüfen.';
    } catch {
      notifyStatus('invalid');
      return 'Adresse konnte nicht überprüft werden.';
    }
  }, [street, plz, city, notifyStatus]);

  // Ref für externen Aufruf bereitstellen
  useEffect(() => {
    if (validateRef) validateRef.current = validate;
  }, [validateRef, validate]);

  const handleChange = (field, value) => {
    const updated = { street, plz, city, [field]: value };
    onChange?.(updated);
    notifyStatus('idle');
  };

  const handleStreetBlur = () => setStreetTouched(true);
  const handleCityBlur = () => setCityTouched(true);

  return (
    <div className={`address-input ${className}`}>    
      <FormLabel htmlFor="locationStreet">Strasse</FormLabel>
      <Input
        id="locationStreet"
        value={street}
        onChange={(e) => handleChange('street', e.target.value)}
        onBlur={handleStreetBlur}
        placeholder="z.B. Bahnhofplatz 1"
        required={required}
        className={`${streetTouched && streetEmpty ? 'input--error' : ''} text__input`}
        onInvalid={onInvalid}
        onInput={onInput}
      />

      <div className="address-input__plz-city">
        <div className="address-input__plz">
          <FormLabel htmlFor="locationPlz">PLZ</FormLabel>
          <Input
            id="locationPlz"
            value={plz}
            onChange={(e) => handleChange('plz', e.target.value)}
            placeholder="8400"
            className="text__input"
          />
        </div>
        <div className="address-input__city">
          <FormLabel htmlFor="locationCity">Ort</FormLabel>
          <Input
            id="locationCity"
            value={city}
            onChange={(e) => handleChange('city', e.target.value)}
            onBlur={handleCityBlur}
            placeholder="Winterthur"
            className={`${cityTouched && cityEmpty ? 'input--error' : ''} text__input`}
          />
        </div>
      </div>

      {validationStatus === 'checking' && (
        <div className="address-validation address-validation--checking">
          Adresse wird überprüft…
        </div>
      )}
      {validationStatus === 'invalid' && (
        <div className="address-validation address-validation--invalid">
          <ErrorIcon size={16} />
          Ungültige Adresse. Bitte korrigieren.
        </div>
      )}

      {geolocation && (
        <button
          type="button"
          className="body Font-M-I-120-D Colors-Accent-01 address-expand__geo"
          onClick={handleGeolocate}
          disabled={geoLoading}
        >
          <Locate size={24} />
          <span>{geoLoading ? 'Standort wird ermittelt…' : 'Aktuellen Standort als meinen Standort festlegen'}</span>
        </button>
      )}
    </div>
  );
};

export default AddressInput;
